import { ReactElement, useMemo } from "react";

import Amex from "chameleon/icons/amex";
import ApplePayMark from "chameleon/icons/apple-pay-mark";
import CartesBancaires from "chameleon/icons/cartes-bancaires";
import CVV from "chameleon/icons/cvv";
import Diners from "chameleon/icons/diner";
import Discover from "chameleon/icons/discover";
import Jcb from "chameleon/icons/jcb";
import Maestro from "chameleon/icons/maestro";
import MasterCard from "chameleon/icons/mastercard";
import UnionPay from "chameleon/icons/union-pay";
import UnknownCard from "chameleon/icons/unknown-card";
import Visa from "chameleon/icons/visa";
import { Text } from "chameleon/lib/components/text/text";
import { Tooltip } from "chameleon/lib/components/tooltip/tooltip";

import {
  displayCardsPaddingClass,
  displayCardsSpanClass,
  displayCardsWrapperClass,
  displayPaymentNameIconClass,
} from "./display-cards.css";

export type DisplayCardsProps = {
  /**
   * What payment types do you support?
   */
  supportedPaymentTypes?: Array<CreditCardNetwork>;
  isPaymentNameWithIcon?: boolean;
  translatedValues?: {
    noCard: string;
    americanExpress: string;
    cvv: string;
    discover: string;
    jcb: string;
    visa: string;
    mastercard: string;
    maestro: string;
    applePay: string;
    unionPay: string;
    dinersClub: string;
    cartesBancaires: string;
    tooltipAriaLabel?: string;
  };
  /**
   * Maximum number of cards to be displayed.
   */
  maxCardsDisplayed?: number;
  /**
   * Maximum width of cards displayed.  Setting this will take precedence over maxCardsDisplayed.
   */
  maxWidth?: number;
  /**
   * How content should be justified.  Defaults to "normal".
   */
  justifyContent?: string;
  /**
   * Optional inline styling for card span.
   */
  displayCardsSpanStyle?: React.CSSProperties;
};

export type CreditCardNetwork =
  | "amex"
  | "visa"
  | "mastercard"
  | "maestro"
  | "discover"
  | "cvv"
  | "jcb"
  | "unionPay"
  | "dinersClub"
  | "cartesBancaires";

export type WalletPaymentTypes = "applePay";

type Asset = {
  alt: string;
  Icon: ReactElement;
};

const createCardAssets = (
  translatedValues?: DisplayCardsProps["translatedValues"]
): Record<CreditCardNetwork | WalletPaymentTypes, Asset> => ({
  amex: {
    alt: translatedValues?.americanExpress ?? "American Express",
    Icon: <Amex sizeVariant={24} isRectangle />,
  },
  cvv: {
    alt: translatedValues?.cvv ?? "CVV",
    Icon: (
      <CVV
        sizeVariant={24}
        isRectangle
        role="presentation"
        aria-hidden={"true"}
      />
    ),
  },
  discover: {
    alt: translatedValues?.discover ?? "Discover",
    Icon: <Discover sizeVariant={24} isRectangle />,
  },
  jcb: {
    alt: translatedValues?.jcb ?? "JCB",
    Icon: <Jcb sizeVariant={24} isRectangle />,
  },
  visa: {
    alt: translatedValues?.visa ?? "Visa",
    Icon: <Visa sizeVariant={24} isRectangle />,
  },
  mastercard: {
    alt: translatedValues?.mastercard ?? "Mastercard",
    Icon: <MasterCard sizeVariant={24} isRectangle />,
  },
  maestro: {
    alt: translatedValues?.maestro ?? "Maestro",
    Icon: <Maestro sizeVariant={24} isRectangle />,
  },
  applePay: {
    alt: translatedValues?.applePay ?? "ApplePay",
    Icon: <ApplePayMark sizeVariant={24} isRectangle />,
  },
  unionPay: {
    alt: translatedValues?.unionPay ?? "UnionPay",
    Icon: <UnionPay sizeVariant={24} isRectangle />,
  },
  dinersClub: {
    alt: translatedValues?.dinersClub ?? "Diners Club",
    Icon: <Diners sizeVariant={24} isRectangle />,
  },
  cartesBancaires: {
    alt: translatedValues?.cartesBancaires ?? "Cartes Bancaires",
    Icon: <CartesBancaires sizeVariant={24} isRectangle />,
  },
});

const calculateDisplayedCards = (
  maxWidth: number | undefined,
  supportedPaymentTypes: Array<CreditCardNetwork>,
  maxCardsDisplayed: number | undefined
): number | undefined => {
  if (!maxWidth) {
    return maxCardsDisplayed;
  }

  const isEven = supportedPaymentTypes.length % 2 === 0;
  const availableWidth = isEven ? maxWidth - 4 : maxWidth; // Adjust for gap in even case
  // Subtract 1 for "+n" element
  return Math.max(1, Math.floor(availableWidth / 40) - 1);
};

export const DisplayCards = ({
  supportedPaymentTypes = [],
  isPaymentNameWithIcon,
  translatedValues,
  maxCardsDisplayed,
  maxWidth,
  justifyContent = "normal",
  displayCardsSpanStyle,
}: DisplayCardsProps) => {
  const calculatedCardsDisplayed = useMemo(
    () =>
      calculateDisplayedCards(
        maxWidth,
        supportedPaymentTypes,
        maxCardsDisplayed
      ),
    [maxWidth, supportedPaymentTypes, maxCardsDisplayed]
  );

  const cardAssets = useMemo(
    () => createCardAssets(translatedValues),
    [translatedValues]
  );

  const renderCardElement = (
    paymentType: CreditCardNetwork,
    isSingleCard: boolean = false
  ) => {
    const cardData = cardAssets[paymentType];
    if (!cardData) return null;

    return (
      <span
        className={`${displayCardsSpanClass} ${isSingleCard && displayCardsPaddingClass} ${isPaymentNameWithIcon && displayPaymentNameIconClass}`}
        style={displayCardsSpanStyle}
        key={cardData.alt}
        aria-label={cardData.alt}
        role="img"
        title={cardData.alt}
      >
        {cardData.Icon}
      </span>
    );
  };

  const renderNoCardsPlaceholder = () => (
    <span
      className={`${displayCardsSpanClass} ${displayCardsPaddingClass}`}
      key="no-card"
    >
      <UnknownCard sizeVariant={24} isRectangle />
    </span>
  );

  const renderMoreCardsTooltip = (visibleCount: number) => {
    if (!calculatedCardsDisplayed) return null;

    const hiddenCards = supportedPaymentTypes.slice(visibleCount);
    const hiddenCardCount = hiddenCards.length;

    if (hiddenCardCount <= 0) return null;

    const tooltipContent = (
      <div>
        {hiddenCards.map((paymentType) => {
          const cardData = cardAssets[paymentType];
          if (!cardData) return null;
          return (
            <div key={cardData.alt} style={{ whiteSpace: "nowrap" }}>
              {cardData.alt}
            </div>
          );
        })}
      </div>
    );

    return (
      <Tooltip
        key="more-cards-tooltip"
        content={tooltipContent}
        ariaLabel={translatedValues?.tooltipAriaLabel}
      >
        <Text
          styleVariant="caption"
          data-dd-action-name="display-cards-overflow-tooltip"
        >
          +{hiddenCardCount}
        </Text>
      </Tooltip>
    );
  };

  const getCardsContent = () => {
    if (supportedPaymentTypes.length === 0) {
      return renderNoCardsPlaceholder();
    }

    const isSingleCard = supportedPaymentTypes.length === 1;

    if (calculatedCardsDisplayed !== undefined) {
      const visibleCount = Math.min(
        calculatedCardsDisplayed,
        supportedPaymentTypes.length
      );
      const visibleCards = supportedPaymentTypes.slice(0, visibleCount);

      const cardElements = visibleCards.map((paymentType) =>
        renderCardElement(paymentType, isSingleCard)
      );

      const tooltipElement = renderMoreCardsTooltip(visibleCount);
      if (tooltipElement) {
        cardElements.push(tooltipElement);
      }

      return cardElements;
    }
    return supportedPaymentTypes.map((paymentType) =>
      renderCardElement(paymentType, isSingleCard)
    );
  };

  return (
    <div
      data-testid="display-cards"
      style={{ justifyContent }}
      className={displayCardsWrapperClass}
    >
      {getCardsContent()}
    </div>
  );
};
